import { useEffect, useState } from "react";
import Badge from "@/components/base/Badge";
import Modal from "@/components/base/Modal";
import { cn } from "@/utils/cn";
import { formatDate, formatFcfa } from "@/utils/format";
import { PAIEMENT_METHODE_LABELS } from "@/store/billing/types";
import { useSuperAdmin } from "../data/SuperAdminProvider";
import {
  AUDIT_ACTION_ICONS,
  AUDIT_STATUT_LABELS,
  AUDIT_STATUT_TONES,
  COMPTE_PAIEMENT_STATUT_LABELS,
  COMPTE_PAIEMENT_STATUT_TONES,
  COMPTE_STATUT_LABELS,
  LIMITE_LABELS,
  formatLimite,
  getFormule,
  type Compte,
  type FormuleLimites,
} from "../data/types";

export type ConsultTab = "abonnement" | "paiements" | "activite" | "audit";

interface CompteConsultModalProps {
  open: boolean;
  onClose: () => void;
  compte: Compte | null;
  initialTab?: ConsultTab;
}

const TABS: { key: ConsultTab; label: string; icon: string }[] = [
  { key: "abonnement", label: "Abonnement", icon: "ri-vip-diamond-line" },
  { key: "paiements", label: "Paiements", icon: "ri-hand-coin-line" },
  { key: "activite", label: "Activité", icon: "ri-history-line" },
  { key: "audit", label: "Journal d'audit", icon: "ri-shield-check-line" },
];

function addMonths(iso: string, months: number): string {
  const date = new Date(iso);
  date.setMonth(date.getMonth() + months);
  return date.toISOString();
}

/** Libellé lisible du moyen de paiement d'abonnement (repli sur la valeur brute). */
function methodeLabel(methode: string): string {
  return (PAIEMENT_METHODE_LABELS as Record<string, string>)[methode] ?? (methode || "—");
}

function TabEmpty({ icon, title, text }: { icon: string; title: string; text: string }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-md bg-background-100/70 px-6 py-10 text-center">
      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-100 text-primary-600">
        <i className={cn(icon, "text-xl")} aria-hidden="true" />
      </span>
      <p className="mt-3 text-sm font-heading font-semibold text-foreground-950">{title}</p>
      <p className="mt-1 max-w-xs text-xs text-foreground-600">{text}</p>
    </div>
  );
}

export default function CompteConsultModal({ open, onClose, compte, initialTab = "abonnement" }: CompteConsultModalProps) {
  const { auditFor } = useSuperAdmin();
  const [tab, setTab] = useState<ConsultTab>(initialTab);

  useEffect(() => {
    if (open) setTab(initialTab);
  }, [open, initialTab]);

  if (!compte) return null;

  const formule = getFormule(compte.formuleId);
  const entries = auditFor(compte.id);

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="xl"
      title={`Consulter — ${compte.nom}`}
      description="Abonnement, paiements, activité et journal d'audit de ce compte."
    >
      <div className="flex flex-col gap-5">
        <div className="flex flex-wrap gap-1 rounded-md bg-background-100 p-1">
          {TABS.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => setTab(item.key)}
              className={cn(
                "inline-flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-md px-3 py-2 text-xs font-label font-medium transition-colors sm:text-sm cursor-pointer",
                tab === item.key
                  ? "bg-background-50 text-foreground-950 shadow-sm"
                  : "text-foreground-600 hover:text-foreground-900"
              )}
            >
              <i className={cn(item.icon, "text-base")} aria-hidden="true" />
              {item.label}
            </button>
          ))}
        </div>

        {tab === "abonnement" && (
          <div className="flex flex-col gap-4">
            {!formule ? (
              <TabEmpty
                icon="ri-vip-diamond-line"
                title="Aucune formule d'abonnement"
                text="Ce compte n'a pas encore de formule associée. Attribuez-en une depuis la modification du compte."
              />
            ) : (
              <>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                    <p className="text-xs text-foreground-500">Formule actuelle</p>
                    <p className="mt-1 font-heading text-lg font-semibold text-foreground-950">{formule.nom}</p>
                  </div>
                  <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                    <p className="text-xs text-foreground-500">Prix mensuel</p>
                    <p className="mt-1 font-heading text-lg font-semibold text-foreground-950">
                      {formule.prixMensuel === 0 ? "Gratuit" : formatFcfa(formule.prixMensuel)}
                    </p>
                  </div>
                  <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                    <p className="text-xs text-foreground-500">Prix annuel</p>
                    <p className="mt-1 font-heading text-lg font-semibold text-foreground-950">
                      {formule.prixAnnuel === 0 ? "Gratuit" : formatFcfa(formule.prixAnnuel)}
                    </p>
                  </div>
                  <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                    <p className="text-xs text-foreground-500">Statut</p>
                    <div className="mt-1.5">
                      <Badge tone="neutral">{COMPTE_STATUT_LABELS[compte.statut]}</Badge>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <div className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-3.5">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-secondary-50 text-secondary-600">
                      <i className="ri-calendar-line text-base" aria-hidden="true" />
                    </span>
                    <div>
                      <p className="text-xs text-foreground-500">Période</p>
                      <p className="text-sm font-label font-medium text-foreground-950">Mensuelle</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-3.5">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                      <i className="ri-play-circle-line text-base" aria-hidden="true" />
                    </span>
                    <div>
                      <p className="text-xs text-foreground-500">Début</p>
                      <p className="text-sm font-label font-medium text-foreground-950">{formatDate(compte.createdAt)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-3.5">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-accent-50 text-accent-700">
                      <i className="ri-stop-circle-line text-base" aria-hidden="true" />
                    </span>
                    <div>
                      <p className="text-xs text-foreground-500">Fin</p>
                      <p className="text-sm font-label font-medium text-foreground-950">
                        {formatDate(addMonths(compte.createdAt, 1))}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="rounded-lg border border-background-200 bg-background-50">
                  <p className="border-b border-background-200 px-4 py-3 text-sm font-heading font-semibold text-foreground-950">
                    Limites de la formule
                  </p>
                  <div className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-3">
                    {(Object.keys(LIMITE_LABELS) as Array<keyof FormuleLimites>).map((key) => (
                      <div key={key} className="flex items-center justify-between gap-2 rounded-md bg-background-100 px-3 py-2">
                        <span className="text-xs text-foreground-600">{LIMITE_LABELS[key]}</span>
                        <span className="text-xs font-label font-medium text-foreground-950">
                          {formatLimite(formule.limites[key])}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {tab === "paiements" && (
          <div className="flex flex-col gap-3">
            {compte.paiements.length === 0 ? (
              <TabEmpty
                icon="ri-hand-coin-line"
                title="Aucun paiement enregistré"
                text="Les paiements d'abonnement de ce compte apparaîtront ici."
              />
            ) : (
              <ul className="flex flex-col divide-y divide-background-200 rounded-lg border border-background-200 bg-background-50">
                {compte.paiements.map((paiement) => (
                  <li key={paiement.id} className="flex items-center justify-between gap-3 px-4 py-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-label font-medium text-foreground-950">
                        {formatFcfa(paiement.montant)}
                      </p>
                      <p className="mt-0.5 truncate text-xs text-foreground-500">
                        {methodeLabel(paiement.methode)} · {paiement.reference || "Sans référence"}
                      </p>
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1">
                      <span className="text-xs text-foreground-500">{formatDate(paiement.date)}</span>
                      {paiement.statut && (
                        <Badge tone={COMPTE_PAIEMENT_STATUT_TONES[paiement.statut]}>
                          {COMPTE_PAIEMENT_STATUT_LABELS[paiement.statut]}
                        </Badge>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {tab === "activite" && (
          <div className="flex flex-col gap-3">
            {compte.activite.length === 0 ? (
              <TabEmpty
                icon="ri-history-line"
                title="Aucune activité"
                text="L'activité de ce compte apparaîtra ici au fil des actions réalisées."
              />
            ) : (
              <ol className="flex flex-col">
                {compte.activite.map((entry, index) => (
                  <li key={entry.id} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-50 text-primary-600">
                        <i className={cn(entry.icone, "text-base")} aria-hidden="true" />
                      </span>
                      {index < compte.activite.length - 1 && <span className="my-1 w-px flex-1 bg-background-200" />}
                    </div>
                    <div className="min-w-0 pb-4">
                      <p className="text-sm font-label font-medium text-foreground-950">{entry.action}</p>
                      <p className="mt-0.5 text-xs text-foreground-600">{entry.description}</p>
                      <p className="mt-1 text-[11px] text-foreground-400">{formatDate(entry.date, true)}</p>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </div>
        )}

        {tab === "audit" && (
          <div className="flex flex-col gap-3">
            {entries.length === 0 ? (
              <TabEmpty
                icon="ri-shield-check-line"
                title="Aucune entrée d'audit"
                text="Les actions administratives concernant ce compte seront tracées ici."
              />
            ) : (
              <ul className="flex flex-col divide-y divide-background-200 rounded-lg border border-background-200 bg-background-50">
                {entries.map((entry) => (
                  <li key={entry.id} className="flex items-start justify-between gap-3 px-4 py-3">
                    <div className="flex min-w-0 items-start gap-3">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-background-100 text-foreground-600">
                        <i className={cn(AUDIT_ACTION_ICONS[entry.actionKey], "text-base")} aria-hidden="true" />
                      </span>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-label font-medium text-foreground-950">{entry.action}</p>
                        <p className="mt-0.5 truncate text-xs text-foreground-500">
                          Par {entry.administrateur} · {formatDate(entry.date, true)}
                        </p>
                      </div>
                    </div>
                    <Badge tone={AUDIT_STATUT_TONES[entry.statut]}>{AUDIT_STATUT_LABELS[entry.statut]}</Badge>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}